import React, { FC, useCallback, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";

const variants = {
    left: {
        x: "100%",
        zIndex: 2,
    },
    right: {
        x: "-100%",
        zIndex: 2,
    },
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
    },
    exit: (direction: number) => ({
        x: direction < 0 ? "-100%" : "100%",
        zIndex: 0,
        transition: {
            duration: 0.3,
        },
    }),
};

const wrap = (min: number, max: number, v: number) => {
    const rangeSize = max - min;
    return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
};

export const usePosition = () => {
    const [[pos, direction], setPos] = useState([0, 0]);
    const onRight = useCallback(() => setPos([pos + -1, -1]), [pos]);
    const onLeft = useCallback(() => setPos([pos + 1, 1]), [pos]);
    return [pos, direction, onLeft, onRight] as const;
};

export interface IPageTransitionProps {
    position: number;
    direction: number;
    disable?: boolean;
    children: React.ReactNode;
}

export const PageTransition: FC<IPageTransitionProps> = props => {
    const { position, direction, disable, children } = props;
    if (disable) {
        return <Wrapper>{children}</Wrapper>;
    }
    const state = direction === 0 ? "center" : direction > 0 ? "right" : "left";
    const index = wrap(0, 2, position);
    const items = ["left", "right"].map(_ => <div key={_}>{children}</div>);

    return (
        <Wrapper>
            <AnimatePresence
                initial={false}
                custom={direction}
                mode="popLayout"
            >
                <motion.div
                    initial={state}
                    animate="center"
                    exit="exit"
                    variants={variants}
                    key={index}
                    custom={direction}
                    transition={{
                        x: {
                            duration: 0.3,
                        },
                    }}
                >
                    <Item>{items[index]}</Item>
                </motion.div>
            </AnimatePresence>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
    overflow: hidden;
    border-radius: inherit;

    .hide-on-animation {
        opacity: 0;
    }

    [style*="transform: translateX(0%)"],
    [style*="transform: none"] {
        .hide-on-animation {
            visibility: visible;
            opacity: 1;
            transition: opacity 0.3s ease-in;
        }
    }
`;

const Item = styled.div`
    top: 0;
    position: absolute;
    width: 100%;
    height: 100%;
`;
